import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Modal, TextInput, ActivityIndicator } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import { getReservationDocsData } from '../../../api/Reservation';
import { useAlertModal } from '../../../common/hooks';
import { msgStr } from '../../../common/constants/Message';
import { API_URL } from '../../../common/constants/AppConstants';
import { postAPICall, deleteAPICall } from '../../../api/BaseAPI';
import LabeledTextInput from '../../../common/components/bohform/LabeledTextInput';
import { BOHButton } from '../../../common/components/bohtoolbar';
import BasicModalContainer from '../../../common/components/basicmodal/BasicModalContainer';
import ModalHeader from '../../../common/components/basicmodal/ModalHeader';
import ModalBody from '../../../common/components/basicmodal/ModalBody';
import ModalFooter from '../../../common/components/basicmodal/ModalFooter';
import { commonModalStyle } from '../../../common/components/basicmodal';

interface Props {
  reservationInfo: any;
}

interface DriverRequest {
  ownerId: string;
  ownerName: string;
  ownerType: string;
  email: string;
  phone: string;
  docs: {
    DRIVER_LICENSE: boolean;
    INSURANCE: boolean;
    WAIVER: boolean;
  };
}

const DocsTab = ({ reservationInfo }: Props) => {
  const [docsData, setDocsData] = useState<any[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<Set<number>>(new Set());
  const [showReRequestModal, setShowReRequestModal] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [driverRequests, setDriverRequests] = useState<DriverRequest[]>([]);
  const [expandedOwners, setExpandedOwners] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [editContactData, setEditContactData] = useState<{ ownerId: string; ownerName: string; email: string; phone: string } | null>(null);
  const [originalContactData, setOriginalContactData] = useState<{ name: string; email: string; phone: string } | null>(null);
  const [editContactLoading, setEditContactLoading] = useState(false);
  const [showDeleteDriverModal, setShowDeleteDriverModal] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState<{ ownerId: string; ownerName: string; documentCount: number } | null>(null);
  const [deleteDriverLoading, setDeleteDriverLoading] = useState(false);
  const [showAddDriverModal, setShowAddDriverModal] = useState(false);
  const [newDrivers, setNewDrivers] = useState<Array<{ name: string; email: string; phone: string }>>([{ name: '', email: '', phone: '' }]);
  const [addDriverLoading, setAddDriverLoading] = useState(false);
  const { showAlert } = useAlertModal();

  const formatDocType = (type?: string) => {
    switch (type) {
      case 'DRIVER_LICENSE':
        return 'Driver License';
      case 'INSURANCE':
        return 'Insurance';
      case 'WAIVER':
        return 'Waiver';
      default:
        return type || 'Document';
    }
  };

  const fetchDocsData = useCallback(() => {
    if (!reservationInfo?.id) return;
    getReservationDocsData({ reservation_id: reservationInfo.id }, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setDocsData(
            (jsonRes?.documents || [])
              .filter((doc: any) => doc.file_url) // Keep all docs including pending_upload for owner grouping
              .map((doc: any) => {
                let parsedMetadata = doc?.metadata ?? {};
                if (typeof parsedMetadata === 'string') {
                  try {
                    parsedMetadata = JSON.parse(parsedMetadata);
                  } catch (_e) {
                    parsedMetadata = {};
                  }
                }
                return { ...doc, metadata: parsedMetadata };
              })
              .sort((a, b) => {
                const aOrder = typeof a?.metadata?.sort_order === 'number' ? a.metadata.sort_order : Infinity;
                const bOrder = typeof b?.metadata?.sort_order === 'number' ? b.metadata.sort_order : Infinity;
                return aOrder - bOrder;
              }),
          );
          break;
        default:
          setDocsData([]);
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  }, [reservationInfo?.id, showAlert]);

  useEffect(() => {
    fetchDocsData();
  }, [fetchDocsData]);

  const formatFileSize = (bytes: number) => {
    if (!bytes) return '0 KB';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const groupDocumentsByOwner = () => {
    const grouped: { [key: string]: { owner: any; documents: any[] } } = {};

    docsData.forEach((doc) => {
      const ownerId = doc?.metadata?.owner?.id || 'unknown';
      if (!grouped[ownerId]) {
        grouped[ownerId] = {
          owner: doc?.metadata?.owner || { id: 'unknown', name: 'Unknown Owner', type: 'UNKNOWN' },
          documents: [],
        };
      }
      grouped[ownerId].documents.push(doc);
    });

    return Object.entries(grouped).sort((a, b) => {
      const aOrder = a[1].documents[0]?.metadata?.sort_order ?? Infinity;
      const bOrder = b[1].documents[0]?.metadata?.sort_order ?? Infinity;
      return aOrder - bOrder;
    });
  };

  const toggleOwnerExpansion = (ownerId: string) => {
    const newExpanded = new Set(expandedOwners);
    if (newExpanded.has(ownerId)) {
      newExpanded.delete(ownerId);
    } else {
      newExpanded.add(ownerId);
    }
    setExpandedOwners(newExpanded);
  };

  const getDocumentTypeSummary = (documents: any[]) => {
    const types = new Set(documents.map(d => d.document_type));
    return Array.from(types).map(type => formatDocType(type)).join(', ');
  };

  const toggleGroupSelection = (documents: any[], event?: any) => {
    if (event) {
      event.stopPropagation();
    }
    const docIds = documents.map(d => d.id);
    const allSelected = docIds.every(id => selectedDocs.has(id));
    const newSelected = new Set(selectedDocs);

    if (allSelected) {
      docIds.forEach(id => newSelected.delete(id));
    } else {
      docIds.forEach(id => newSelected.add(id));
    }
    setSelectedDocs(newSelected);
  };

  const isGroupSelected = (documents: any[]) => {
    const docIds = documents.map(d => d.id);
    return docIds.length > 0 && docIds.every(id => selectedDocs.has(id));
  };

  const isGroupPartiallySelected = (documents: any[]) => {
    const docIds = documents.map(d => d.id);
    const selectedCount = docIds.filter(id => selectedDocs.has(id)).length;
    return selectedCount > 0 && selectedCount < docIds.length;
  };

  const toggleDocSelection = (docId: number) => {
    const newSelected = new Set(selectedDocs);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedDocs(newSelected);
  };

  const openDeleteModal = (doc: any) => {
    setDocumentToDelete(doc);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDocumentToDelete(null);
  };

  const deleteDocument = async () => {
    if (!documentToDelete) return;

    setDeleteLoading(true);

    deleteAPICall(`reservations/customerdocument/${documentToDelete.id}`, (jsonRes, status, error) => {
      setDeleteLoading(false);
      switch (status) {
        case 200:
          showAlert('success', 'Document deleted successfully');
          closeDeleteModal();
          fetchDocsData();
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  const openEditContactModal = (owner: any) => {
    const name = owner.name || '';
    const email = owner.email || '';
    const phone = owner.phone || '';
    
    setEditContactData({
      ownerId: owner.id,
      ownerName: name,
      email,
      phone,
    });
    setOriginalContactData({ name, email, phone });
    setShowEditContactModal(true);
  };

  const closeEditContactModal = () => {
    setShowEditContactModal(false);
    setEditContactData(null);
    setOriginalContactData(null);
  };

  const openDeleteDriverModal = (owner: any, documents: any[]) => {
    setDriverToDelete({
      ownerId: owner.id,
      ownerName: owner.name,
      documentCount: documents.filter(d => d.file_url !== 'pending_upload').length,
    });
    setShowDeleteDriverModal(true);
  };

  const closeDeleteDriverModal = () => {
    setShowDeleteDriverModal(false);
    setDriverToDelete(null);
  };

  const deleteDriver = async () => {
    if (!driverToDelete || !reservationInfo?.id) return;

    setDeleteDriverLoading(true);

    const payload = {
      reservation_id: reservationInfo.id,
      owner_id: driverToDelete.ownerId,
    };

    postAPICall('reservations/deletedriver', payload, (jsonRes, status, error) => {
      setDeleteDriverLoading(false);
      switch (status) {
        case 200:
          showAlert('success', `Driver and ${jsonRes.deleted || 0} document(s) deleted successfully`);
          closeDeleteDriverModal();
          fetchDocsData();
          setSelectedDocs(new Set());
          break;
        case 404:
          showAlert('error', jsonRes.error || 'No documents found for this driver');
          closeDeleteDriverModal();
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  const openAddDriverModal = () => {
    setNewDrivers([{ name: '', email: '', phone: '' }]);
    setShowAddDriverModal(true);
  };

  const closeAddDriverModal = () => {
    setShowAddDriverModal(false);
    setNewDrivers([{ name: '', email: '', phone: '' }]);
  };

  const addDriverRow = () => {
    setNewDrivers([...newDrivers, { name: '', email: '', phone: '' }]);
  };

  const removeDriverRow = (index: number) => {
    if (newDrivers.length > 1) {
      setNewDrivers(newDrivers.filter((_, i) => i !== index));
    }
  };

  const updateDriverField = (index: number, field: 'name' | 'email' | 'phone', value: string) => {
    const updated = [...newDrivers];
    updated[index][field] = value;
    setNewDrivers(updated);
  };

  const isValidEmail = (email: string, allowEmpty: boolean = true) => {
    if (!email || !email.trim()) return allowEmpty;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  const hasDuplicateNameInModal = (name: string, currentIndex: number) => {
    const trimmedName = name.trim().toLowerCase();
    if (!trimmedName) return false;
    return newDrivers.some((driver, index) => 
      index !== currentIndex && driver.name.trim().toLowerCase() === trimmedName
    );
  };

  const hasDuplicateEmailInModal = (email: string, currentIndex: number) => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) return false;
    return newDrivers.some((driver, index) => 
      index !== currentIndex && driver.email.trim().toLowerCase() === trimmedEmail
    );
  };

  const hasDuplicateNameInExisting = (name: string) => {
    const trimmedName = name.trim().toLowerCase();
    if (!trimmedName) return false;
    return groupDocumentsByOwner().some(([_, group]) => 
      group.owner.name.trim().toLowerCase() === trimmedName
    );
  };

  const hasDuplicateEmailInExisting = (email: string) => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) return false;
    return groupDocumentsByOwner().some(([_, group]) => 
      group.owner.email && group.owner.email.trim().toLowerCase() === trimmedEmail
    );
  };

  const canAddDrivers = () => {
    return newDrivers.every((driver, index) => {
      const hasName = driver.name.trim().length > 0;
      const hasContact = driver.email.trim().length > 0 || driver.phone.trim().length > 0;
      const validEmail = !driver.email || isValidEmail(driver.email);
      const noDuplicateNameInModal = !hasDuplicateNameInModal(driver.name, index);
      const noDuplicateEmailInModal = !driver.email || !hasDuplicateEmailInModal(driver.email, index);
      const noDuplicateNameInExisting = !hasDuplicateNameInExisting(driver.name);
      const noDuplicateEmailInExisting = !driver.email || !hasDuplicateEmailInExisting(driver.email);
      
      return hasName && hasContact && validEmail && 
             noDuplicateNameInModal && noDuplicateEmailInModal &&
             noDuplicateNameInExisting && noDuplicateEmailInExisting;
    });
  };

  const addDrivers = async () => {
    if (!reservationInfo?.id || !canAddDrivers()) return;

    setAddDriverLoading(true);

    const users = newDrivers.map(driver => ({
      name: driver.name.trim(),
      email: driver.email.trim() || undefined,
      phone: driver.phone.trim() || undefined,
    }));

    const payload = {
      reservation_id: reservationInfo.id,
      users,
    };

    postAPICall('reservations/createdriverdocuments', payload, (jsonRes, status, error) => {
      setAddDriverLoading(false);
      switch (status) {
        case 201:
          showAlert('success', `${users.length} driver(s) added successfully with ${jsonRes.inserted || 0} documents`);
          closeAddDriverModal();
          fetchDocsData();
          break;
        case 404:
          showAlert('error', jsonRes.error || 'Reservation not found');
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  const updateDriverContact = async () => {
    if (!editContactData || !originalContactData) return;

    const currentName = editContactData.ownerName?.trim() || '';
    const currentEmail = editContactData.email?.trim() || '';
    const currentPhone = editContactData.phone?.trim() || '';
    const originalName = originalContactData.name?.trim() || '';
    const originalEmail = originalContactData.email?.trim() || '';
    const originalPhone = originalContactData.phone?.trim() || '';

    const nameChanged = currentName !== originalName;
    const emailChanged = currentEmail !== originalEmail;
    const phoneChanged = currentPhone !== originalPhone;

    if (!nameChanged && !emailChanged && !phoneChanged) {
      showAlert('info', 'No changes detected');
      return;
    }

    if (!currentName) {
      showAlert('error', 'Please provide a name');
      return;
    }

    if (!currentEmail && !currentPhone) {
      showAlert('error', 'Please provide at least email or phone');
      return;
    }

    if (currentEmail && !isValidEmail(currentEmail)) {
      showAlert('error', 'Please enter a valid email address');
      return;
    }

    setEditContactLoading(true);

    const payload: any = {
      reservation_id: reservationInfo.id,
      owner_id: editContactData.ownerId,
    };

    if (nameChanged) {
      payload.name = currentName;
    }

    if (emailChanged) {
      payload.email = currentEmail;
    }

    if (phoneChanged) {
      payload.phone = currentPhone;
    }

    postAPICall('reservations/updatedrivercontact', payload, (jsonRes, status, error) => {
      setEditContactLoading(false);
      switch (status) {
        case 200:
          showAlert('success', 'Driver contact updated successfully');
          closeEditContactModal();
          fetchDocsData();
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  const openReRequestModal = () => {
    if (selectedDocs.size === 0) {
      showAlert('error', 'Please select at least one document');
      return;
    }
    
    const groupedByOwner = groupDocumentsByOwner();
    const requests: DriverRequest[] = [];

    groupedByOwner.forEach(([ownerId, group]) => {
      const hasSelectedDocs = group.documents.some(doc => selectedDocs.has(doc.id));
      if (hasSelectedDocs) {
        requests.push({
          ownerId,
          ownerName: group.owner.name,
          ownerType: group.owner.identifier === 'primary' ? 'Primary' : 'Additional',
          email: group.owner.email || '',
          phone: group.owner.phone || '',
          docs: {
            DRIVER_LICENSE: false,
            INSURANCE: false,
            WAIVER: false,
          },
        });
      }
    });

    setDriverRequests(requests);
    setShowReRequestModal(true);
  };

  const updateDriverRequest = (index: number, field: keyof DriverRequest, value: any) => {
    const updated = [...driverRequests];
    updated[index] = { ...updated[index], [field]: value };
    setDriverRequests(updated);
  };

  const toggleDriverDocType = (index: number, docType: 'DRIVER_LICENSE' | 'INSURANCE' | 'WAIVER') => {
    const updated = [...driverRequests];
    updated[index] = {
      ...updated[index],
      docs: {
        ...updated[index].docs,
        [docType]: !updated[index].docs[docType],
      },
    };
    setDriverRequests(updated);
  };

  const hasSelectedDocs = (docs: { DRIVER_LICENSE: boolean; INSURANCE: boolean; WAIVER: boolean }) => {
    return Object.values(docs).some(selected => selected === true);
  };

  const isSendButtonDisabled = () => {
    if (driverRequests.length === 0) return true;
    if (requestLoading) return true;
    
    // Check if ANY driver has invalid data
    const hasInvalidDriver = driverRequests.some(request => {
      const emailInvalid = !isValidEmail(request.email);
      const noDocsSelected = !hasSelectedDocs(request.docs);
      return emailInvalid || noDocsSelected;
    });
    
    return hasInvalidDriver;
  };

  const sendReRequest = async () => {
    // Final validation before sending (should not reach here if button is properly disabled)
    if (isSendButtonDisabled()) {
      return;
    }

    setRequestLoading(true);

    // Build doc_requests array matching Vue.js format
    const doc_requests = driverRequests.map(req => {
      // Get the actual document objects for this owner from docsData
      const ownerDocs = docsData.filter(doc => 
        doc?.metadata?.owner?.name === req.ownerName
      );

      // Filter docs based on selected types and map to simplified format
      const selectedDocs = ownerDocs
        .filter(doc => {
          const docType = doc.document_type;
          return req.docs[docType as keyof typeof req.docs];
        })
        .map(doc => ({
          id: doc.id,
          type: doc.document_type,
        }));

      return {
        owner_name: req.ownerName,
        driver_type: req.ownerType,
        email: req.email,
        phone: req.phone || null,
        docs: selectedDocs,
      };
    }).filter(req => req.docs.length > 0);

    if (doc_requests.length === 0) {
      showAlert('error', 'No documents to request');
      setRequestLoading(false);
      return;
    }

    const payload = {
      reservation_id: reservationInfo.id,
      doc_requests,
    };

    postAPICall('reservations/senddriverdocumentsrequests', payload, (jsonRes, status, error) => {
      setRequestLoading(false);
      switch (status) {
        case 200:
          showAlert('success', 'Upload / sign link sent!');
          setShowReRequestModal(false);
          setSelectedDocs(new Set());
          fetchDocsData();
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  return (
    <View style={styles.container}>
      {docsData.length > 0 && (
        <View style={styles.headerContainer}>
          <Text style={styles.docsHeader}>Drivers</Text>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <BOHButton
              label="Add Driver"
              onPress={openAddDriverModal}
            />
            {selectedDocs.size > 0 && (
              <BOHButton
                label={`Re-Request (${selectedDocs.size})`}
                onPress={openReRequestModal}
              />
            )}
          </View>
        </View>
      )}
      <ScrollView 
        showsVerticalScrollIndicator={true}
        persistentScrollbar={true}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        {docsData.length > 0 ? (
          <View style={styles.docsContainer}>
            {groupDocumentsByOwner().map(([ownerId, group]) => {
              const isExpanded = expandedOwners.has(ownerId);
              const isPrimary = group.owner.identifier === 'primary';
              
              return (
                <View key={ownerId} style={styles.accordionContainer}>
                  <View style={styles.accordionHeader}>
                    <TouchableOpacity
                      style={styles.accordionCheckbox}
                      onPress={(e) => toggleGroupSelection(group.documents, e)}
                    >
                      <FontAwesome5
                        name={
                          isGroupSelected(group.documents)
                            ? 'check-square'
                            : isGroupPartiallySelected(group.documents)
                            ? 'minus-square'
                            : 'square'
                        }
                        size={20}
                        color={
                          isGroupSelected(group.documents) || isGroupPartiallySelected(group.documents)
                            ? '#007bff'
                            : '#ccc'
                        }
                      />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.accordionHeaderLeft}
                      onPress={() => toggleOwnerExpansion(ownerId)}
                    >
                      <FontAwesome5
                        name="user-circle"
                        size={20}
                        color={isPrimary ? '#007bff' : '#6c757d'}
                        style={{ marginRight: 10 }}
                      />
                      <View style={styles.accordionHeaderText}>
                        <View style={styles.ownerNameRow}>
                          <Text style={styles.ownerName}>{group.owner.name}</Text>
                          {isPrimary && (
                            <View style={styles.primaryBadge}>
                              <Text style={styles.primaryBadgeText}>Primary</Text>
                            </View>
                          )}
                        </View>
                        {(group.owner.email || group.owner.phone) ? (
                          <Text style={styles.ownerContactText}>
                            {[group.owner.email, group.owner.phone].filter(Boolean).join(' · ')}
                          </Text>
                        ) : null}
                        <Text style={styles.ownerSubtext}>
                          {group.documents.filter(d => d.file_url !== 'pending_upload').length} document{group.documents.filter(d => d.file_url !== 'pending_upload').length !== 1 ? 's' : ''} · {getDocumentTypeSummary(group.documents)}
                        </Text>
                      </View>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.editContactButton}
                      onPress={(e) => {
                        e.stopPropagation();
                        openEditContactModal(group.owner);
                      }}
                    >
                      <FontAwesome5
                        name="edit"
                        size={16}
                        color="#007bff"
                      />
                    </TouchableOpacity>
                    {!isPrimary && (
                      <TouchableOpacity
                        style={styles.deleteDriverButton}
                        onPress={(e) => {
                          e.stopPropagation();
                          openDeleteDriverModal(group.owner, group.documents);
                        }}
                      >
                        <FontAwesome5
                          name="trash"
                          size={16}
                          color="#dc3545"
                        />
                      </TouchableOpacity>
                    )}
                    <TouchableOpacity
                      style={styles.accordionHeaderRight}
                      onPress={() => toggleOwnerExpansion(ownerId)}
                    >
                      <FontAwesome5
                        name={isExpanded ? 'chevron-down' : 'chevron-right'}
                        size={16}
                        color="#666"
                      />
                    </TouchableOpacity>
                  </View>

                  {isExpanded && (
                    <View style={styles.accordionContent}>
                      {group.documents.filter(doc => doc.file_url !== 'pending_upload').length > 0 ? (
                        group.documents
                          .filter(doc => doc.file_url !== 'pending_upload')
                          .map((doc) => (
                            <View key={doc.id} style={styles.docCard}>
                              <View style={styles.docInfo}>
                                <FontAwesome5 name="file-alt" size={22} color="#4b627bff" />
                                <View style={styles.docTextContainer}>
                                  <View style={styles.badgeRow}>
                                    <Text style={styles.docName} numberOfLines={1}>
                                      {doc.file_name || 'Unnamed File'}
                                    </Text>
                                    <View style={styles.badge}>
                                      <Text style={styles.badgeText}>{formatDocType(doc.document_type)}</Text>
                                    </View>
                                  </View>
                                  <Text style={styles.docSize}>{formatFileSize(doc.file_size)}</Text>
                                </View>
                              </View>
                              <View style={styles.docActions}>
                                <TouchableOpacity
                                  style={styles.viewButton}
                                  onPress={() => doc.file_url && window.open(API_URL + '/uploads/' + doc.file_url, '_blank')}
                                >
                                  <Text style={styles.viewButtonText}>View</Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                  style={styles.deleteButton}
                                  onPress={() => openDeleteModal(doc)}
                                >
                                  <FontAwesome5 name="trash" size={16} color="#dc3545" />
                                </TouchableOpacity>
                              </View>
                            </View>
                          ))
                      ) : (
                        <View style={styles.noPendingDocs}>
                          <Text style={styles.noPendingDocsText}>No uploaded documents yet. Select to request documents.</Text>
                        </View>
                      )}
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        ) : (
          <View style={styles.emptyState}>
            {' '}
            <FontAwesome5 name="file" size={48} color="#ccc" />{' '}
            <Text style={styles.emptyText}>No documents attached</Text>{' '}
          </View>
        )}
      </ScrollView>

      <Modal
        visible={showDeleteModal}
        transparent={true}
        animationType="fade"
        onRequestClose={closeDeleteModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.deleteModalContent}>
            <View style={styles.deleteModalHeader}>
              <Text style={styles.deleteModalTitle}>Delete Document</Text>
              <TouchableOpacity onPress={closeDeleteModal}>
                <FontAwesome5 name="times" size={24} color="#000" />
              </TouchableOpacity>
            </View>

            <View style={styles.deleteModalBody}>
              <FontAwesome5 name="exclamation-triangle" size={48} color="#dc3545" style={{ marginBottom: 16 }} />
              <Text style={styles.deleteModalText}>
                Are you sure you want to delete this document?
              </Text>
              {documentToDelete && (
                <Text style={styles.deleteModalDocName}>
                  {documentToDelete.file_name || 'Unnamed File'}
                </Text>
              )}
              <Text style={styles.deleteModalWarning}>
                This action cannot be undone.
              </Text>
            </View>

            <View style={styles.deleteModalFooter}>
              <TouchableOpacity
                style={styles.deleteCancelButton}
                onPress={closeDeleteModal}
                disabled={deleteLoading}
              >
                <Text style={styles.deleteCancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.deleteConfirmButton, deleteLoading && styles.deleteConfirmButtonDisabled]}
                onPress={deleteDocument}
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.deleteConfirmButtonText}>Delete</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={showDeleteDriverModal}
        transparent={true}
        animationType="fade"
        onRequestClose={closeDeleteDriverModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.deleteModalContent}>
            <View style={styles.deleteModalHeader}>
              <Text style={styles.deleteModalTitle}>Delete Driver</Text>
              <TouchableOpacity onPress={closeDeleteDriverModal}>
                <FontAwesome5 name="times" size={24} color="#000" />
              </TouchableOpacity>
            </View>

            <View style={styles.deleteModalBody}>
              <FontAwesome5 name="exclamation-triangle" size={48} color="#dc3545" style={{ marginBottom: 16 }} />
              <Text style={styles.deleteModalText}>
                Are you sure you want to delete this driver?
              </Text>
              {driverToDelete && (
                <>
                  <Text style={styles.deleteModalDocName}>
                    {driverToDelete.ownerName}
                  </Text>
                  <Text style={styles.deleteModalSubtext}>
                    This will delete {driverToDelete.documentCount} document{driverToDelete.documentCount !== 1 ? 's' : ''} associated with this driver.
                  </Text>
                </>
              )}
              <Text style={styles.deleteModalWarning}>
                This action cannot be undone.
              </Text>
            </View>

            <View style={styles.deleteModalFooter}>
              <TouchableOpacity
                style={styles.deleteCancelButton}
                onPress={closeDeleteDriverModal}
                disabled={deleteDriverLoading}
              >
                <Text style={styles.deleteCancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.deleteConfirmButton, deleteDriverLoading && styles.deleteConfirmButtonDisabled]}
                onPress={deleteDriver}
                disabled={deleteDriverLoading}
              >
                {deleteDriverLoading ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.deleteConfirmButtonText}>Delete Driver</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={showReRequestModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowReRequestModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Send Document Request</Text>
              <TouchableOpacity onPress={() => setShowReRequestModal(false)}>
                <FontAwesome5 name="times" size={24} color="#000" />
              </TouchableOpacity>
            </View>

            <View style={styles.modalDescriptionContainer}>
              <Text style={styles.modalDescriptionText}>
                Review the selected drivers and choose which documents to request. Email and phone are pre-filled from their profile.
              </Text>
            </View>

            <ScrollView style={styles.modalBody}>
              {driverRequests.map((request, index) => (
                <View key={request.ownerId} style={styles.driverCard}>
                  <View style={styles.driverCardHeader}>
                    <FontAwesome5 name="user" size={16} color="#333" style={{ marginRight: 8 }} />
                    <Text style={styles.driverName}>{request.ownerName}</Text>
                    <View style={styles.driverTypeBadge}>
                      <Text style={styles.driverTypeBadgeText}>{request.ownerType}</Text>
                    </View>
                  </View>

                  <View style={styles.driverInputRow}>
                    <View style={styles.driverInputHalf}>
                      <Text style={styles.driverInputLabel}>Email *</Text>
                      <TextInput
                        style={[styles.input, request.email && !isValidEmail(request.email) && styles.inputError]}
                        value={request.email}
                        onChangeText={(text) => updateDriverRequest(index, 'email', text)}
                        placeholder="email@example.com"
                        keyboardType="email-address"
                        autoCapitalize="none"
                      />
                      {request.email && !isValidEmail(request.email) && (
                        <Text style={styles.errorText}>Invalid email</Text>
                      )}
                    </View>
                    <View style={styles.driverInputHalf}>
                      <Text style={styles.driverInputLabel}>Phone (optional)</Text>
                      <TextInput
                        style={styles.input}
                        value={request.phone}
                        onChangeText={(text) => updateDriverRequest(index, 'phone', text)}
                        placeholder="Phone (optional)"
                        keyboardType="phone-pad"
                      />
                    </View>
                  </View>

                  <View>
                    <View style={styles.driverDocsRow}>
                      <TouchableOpacity
                        style={styles.docCheckboxItem}
                        onPress={() => toggleDriverDocType(index, 'DRIVER_LICENSE')}
                      >
                        <FontAwesome5
                          name={request.docs.DRIVER_LICENSE ? 'check-square' : 'square'}
                          size={18}
                          color={request.docs.DRIVER_LICENSE ? '#007bff' : '#ccc'}
                        />
                        <Text style={styles.docCheckboxLabel}>License</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.docCheckboxItem}
                        onPress={() => toggleDriverDocType(index, 'INSURANCE')}
                      >
                        <FontAwesome5
                          name={request.docs.INSURANCE ? 'check-square' : 'square'}
                          size={18}
                          color={request.docs.INSURANCE ? '#007bff' : '#ccc'}
                        />
                        <Text style={styles.docCheckboxLabel}>Insurance</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.docCheckboxItem}
                        onPress={() => toggleDriverDocType(index, 'WAIVER')}
                      >
                        <FontAwesome5
                          name={request.docs.WAIVER ? 'check-square' : 'square'}
                          size={18}
                          color={request.docs.WAIVER ? '#007bff' : '#ccc'}
                        />
                        <Text style={styles.docCheckboxLabel}>Waiver</Text>
                      </TouchableOpacity>
                    </View>
                    {!hasSelectedDocs(request.docs) && (
                      <Text style={styles.errorText}>Select at least one document type</Text>
                    )}
                  </View>
                </View>
              ))}
            </ScrollView>

            <View style={styles.modalFooter}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => setShowReRequestModal(false)}
                disabled={requestLoading}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.sendButton, isSendButtonDisabled() && styles.sendButtonDisabled]}
                onPress={sendReRequest}
                disabled={isSendButtonDisabled()}
              >
                {requestLoading ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.sendButtonText}>Send</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={showEditContactModal}
        transparent={true}
        animationType="fade"
        onRequestClose={closeEditContactModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.editContactModalContent}>
            <View style={styles.editContactModalHeader}>
              <Text style={styles.editContactModalTitle}>Edit Driver Contact</Text>
              <TouchableOpacity onPress={closeEditContactModal}>
                <FontAwesome5 name="times" size={24} color="#000" />
              </TouchableOpacity>
            </View>

            <View style={styles.editContactModalBody}>
              {editContactData && (
                <>
                  <View style={styles.editContactInputGroup}>
                    <Text style={styles.editContactLabel}>Name *</Text>
                    <TextInput
                      style={styles.editContactInput}
                      value={editContactData.ownerName}
                      onChangeText={(text) => setEditContactData({ ...editContactData, ownerName: text })}
                      placeholder="Driver Name"
                    />
                  </View>

                  <Text style={styles.editContactHelperText}>
                    Provide at least one contact method (email or phone)
                  </Text>

                  <View style={styles.editContactInputGroup}>
                    <Text style={styles.editContactLabel}>Email</Text>
                    <TextInput
                      style={[
                        styles.editContactInput,
                        editContactData.email && !isValidEmail(editContactData.email) && styles.inputError
                      ]}
                      value={editContactData.email}
                      onChangeText={(text) => setEditContactData({ ...editContactData, email: text })}
                      placeholder="email@example.com"
                      keyboardType="email-address"
                      autoCapitalize="none"
                    />
                    {editContactData.email && !isValidEmail(editContactData.email) && (
                      <Text style={styles.errorText}>Invalid email address</Text>
                    )}
                  </View>

                  <View style={styles.editContactInputGroup}>
                    <Text style={styles.editContactLabel}>Phone</Text>
                    <TextInput
                      style={styles.editContactInput}
                      value={editContactData.phone}
                      onChangeText={(text) => setEditContactData({ ...editContactData, phone: text })}
                      placeholder="Phone number"
                      keyboardType="phone-pad"
                    />
                  </View>
                </>
              )}
            </View>

            <View style={styles.editContactModalFooter}>
              <TouchableOpacity
                style={styles.editContactCancelButton}
                onPress={closeEditContactModal}
                disabled={editContactLoading}
              >
                <Text style={styles.editContactCancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.editContactSaveButton,
                  (editContactLoading || (editContactData && originalContactData && (() => {
                    const currentName = editContactData.ownerName?.trim() || '';
                    const currentEmail = editContactData.email?.trim() || '';
                    const currentPhone = editContactData.phone?.trim() || '';
                    const originalName = originalContactData.name?.trim() || '';
                    const originalEmail = originalContactData.email?.trim() || '';
                    const originalPhone = originalContactData.phone?.trim() || '';
                    
                    const noChanges = currentName === originalName && currentEmail === originalEmail && currentPhone === originalPhone;
                    const noName = !currentName;
                    const bothEmpty = !currentEmail && !currentPhone;
                    const invalidEmail = currentEmail && !isValidEmail(currentEmail);
                    
                    return noChanges || noName || bothEmpty || invalidEmail;
                  })())) && styles.editContactSaveButtonDisabled
                ]}
                onPress={updateDriverContact}
                disabled={editContactLoading || (editContactData && originalContactData && (() => {
                  const currentName = editContactData.ownerName?.trim() || '';
                  const currentEmail = editContactData.email?.trim() || '';
                  const currentPhone = editContactData.phone?.trim() || '';
                  const originalName = originalContactData.name?.trim() || '';
                  const originalEmail = originalContactData.email?.trim() || '';
                  const originalPhone = originalContactData.phone?.trim() || '';
                  
                  const noChanges = currentName === originalName && currentEmail === originalEmail && currentPhone === originalPhone;
                  const noName = !currentName;
                  const bothEmpty = !currentEmail && !currentPhone;
                  const invalidEmail = currentEmail && !isValidEmail(currentEmail);
                  
                  return noChanges || noName || bothEmpty || invalidEmail;
                })())}
              >
                {editContactLoading ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.editContactSaveButtonText}>Save</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={showAddDriverModal}
        transparent={true}
        animationType="none"
        onRequestClose={closeAddDriverModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.editContactModalContent}>
            <View style={styles.editContactModalHeader}>
              <Text style={styles.editContactModalTitle}>Add Driver(s)</Text>
              <TouchableOpacity onPress={closeAddDriverModal}>
                <FontAwesome5 name="times" size={24} color="#000" />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.editContactModalBody}>
              <Text style={styles.editContactHelperText}>
                Add one or more drivers. Each driver must have a name and at least one contact method (email or phone).
              </Text>
            {newDrivers.map((driver, index) => (
              <View key={index} style={localStyles.driverFormRow}>
                <View style={localStyles.driverFormHeader}>
                  <Text style={localStyles.driverFormTitle}>Driver {index + 1}</Text>
                  {newDrivers.length > 1 && (
                    <TouchableOpacity onPress={() => removeDriverRow(index)}>
                      <FontAwesome5 name="trash" size={16} color="#dc3545" />
                    </TouchableOpacity>
                  )}
                </View>

                <View style={localStyles.inputRow}>
                  <View style={[localStyles.inputContainer, { flex: 1 }]}>
                    <Text style={commonModalStyle.label}>Name *</Text>
                    <TextInput
                      style={[
                        commonModalStyle.input,
                        { width: '100%' },
                        (hasDuplicateNameInModal(driver.name, index) || hasDuplicateNameInExisting(driver.name)) && localStyles.inputError
                      ]}
                      value={driver.name}
                      onChangeText={(text) => updateDriverField(index, 'name', text)}
                      placeholder="Driver Name"
                      placeholderTextColor="#ccc"
                    />
                    {hasDuplicateNameInModal(driver.name, index) && (
                      <Text style={commonModalStyle.message}>Duplicate name in form</Text>
                    )}
                    {!hasDuplicateNameInModal(driver.name, index) && hasDuplicateNameInExisting(driver.name) && (
                      <Text style={commonModalStyle.message}>Driver with this name already exists</Text>
                    )}
                  </View>

                  <View style={[localStyles.inputContainer, { flex: 1 }]}>
                    <Text style={commonModalStyle.label}>Email</Text>
                    <TextInput
                      style={[
                        commonModalStyle.input,
                        { width: '100%' },
                        ((driver.email && !isValidEmail(driver.email)) || hasDuplicateEmailInModal(driver.email, index) || hasDuplicateEmailInExisting(driver.email)) && localStyles.inputError
                      ]}
                      value={driver.email}
                      onChangeText={(text) => updateDriverField(index, 'email', text)}
                      placeholder="email@example.com"
                      keyboardType="email-address"
                      autoCapitalize="none"
                      placeholderTextColor="#ccc"
                    />
                    {driver.email && !isValidEmail(driver.email) && (
                      <Text style={commonModalStyle.message}>Invalid email address</Text>
                    )}
                    {driver.email && isValidEmail(driver.email) && hasDuplicateEmailInModal(driver.email, index) && (
                      <Text style={commonModalStyle.message}>Duplicate email in form</Text>
                    )}
                    {driver.email && isValidEmail(driver.email) && !hasDuplicateEmailInModal(driver.email, index) && hasDuplicateEmailInExisting(driver.email) && (
                      <Text style={commonModalStyle.message}>Driver with this email already exists</Text>
                    )}
                  </View>

                  <View style={[localStyles.inputContainer, { flex: 1 }]}>
                    <Text style={commonModalStyle.label}>Phone</Text>
                    <TextInput
                      style={[commonModalStyle.input, { width: '100%' }]}
                      value={driver.phone}
                      onChangeText={(text) => updateDriverField(index, 'phone', text)}
                      placeholder="Phone number"
                      keyboardType="phone-pad"
                      placeholderTextColor="#ccc"
                    />
                  </View>
                </View>

                {index < newDrivers.length - 1 && (
                  <View style={localStyles.driverFormDivider} />
                )}
              </View>
            ))}

              <BOHButton
                label="Add Another Driver"
                onPress={addDriverRow}
                style={{ marginTop: 16 }}
              />
            </ScrollView>

            <View style={styles.editContactModalFooter}>
              {addDriverLoading ? (
                <ActivityIndicator size="small" color="#2e96e1" />
              ) : (
                <BOHButton
                  label="Add"
                  onPress={addDrivers}
                  disabled={!canAddDrivers()}
                />
              )}
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { 
    flex: 1, 
    backgroundColor: '#ffffff', 
    width: "100%", 
    maxHeight: 510,
    borderRadius: 8,
    overflow: 'hidden'
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    paddingBottom: 12,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  docsHeader: { 
    fontSize: 18, 
    fontWeight: 'bold', 
    color: '#333',
  },
  reRequestButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#28a745',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  reRequestButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  addDriverButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#007bff',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  addDriverButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  scrollView: {
    flex: 1
  },
  scrollContent: {
    paddingBottom: 16
  },
  docsContainer: { 
    paddingRight: 16, 
    paddingLeft: 16,
    paddingTop: 8
  },
  accordionContainer: {
    marginBottom: 12,
    borderRadius: 8,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    overflow: 'hidden',
  },
  accordionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    backgroundColor: '#f8f9fa',
  },
  accordionCheckbox: {
    marginRight: 12,
    padding: 4,
  },
  accordionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  accordionHeaderText: {
    flex: 1,
  },
  ownerNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  ownerName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  primaryBadge: {
    backgroundColor: '#007bff',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  primaryBadgeText: {
    fontSize: 11,
    color: '#fff',
    fontWeight: '600',
  },
  ownerContactText: {
    fontSize: 13,
    color: '#6c757d',
    marginBottom: 2,
  },
  ownerSubtext: {
    fontSize: 13,
    color: '#6c757d',
  },
  accordionHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  accordionContent: {
    padding: 12,
    paddingTop: 0,
    backgroundColor: '#fff',
  },
  docCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f9f9f9',
    padding: 12,
    borderRadius: 8,
    marginTop: 10,
  },
  docInfo: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  docTextContainer: { marginLeft: 10, flexShrink: 1 },
  badgeRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  docName: { fontSize: 16, color: '#333', fontWeight: '500' },
  badge: {
    backgroundColor: '#e8f1ff',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  badgeText: { fontSize: 12, color: '#2f6dce', fontWeight: '600' },
  docOwner: { fontSize: 13, color: '#555', marginTop: 2 },
  docSize: { fontSize: 13, color: '#777', marginTop: 2 },
  docActions: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  viewButton: {
    backgroundColor: '#007bff',
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 6,
  },
  viewButtonText: { color: '#fff', fontSize: 14, fontWeight: '600' },
  deleteButton: {
    padding: 8,
    borderRadius: 4,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#dc3545',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: { marginTop: 16, fontSize: 16, color: '#666' },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    maxWidth: 500,
    maxHeight: '80%',
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 5,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    paddingBottom: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#000',
  },
  modalDescriptionContainer: {
    paddingHorizontal: 20,
    paddingBottom: 16,
  },
  modalDescriptionText: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  modalBody: {
    paddingHorizontal: 20,
    maxHeight: 450,
  },
  driverCard: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  driverCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  driverName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginRight: 8,
  },
  driverTypeBadge: {
    backgroundColor: '#f0f0f0',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
  },
  driverTypeBadgeText: {
    fontSize: 12,
    color: '#666',
    fontWeight: '500',
  },
  driverInputRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
  },
  driverInputHalf: {
    flex: 1,
  },
  driverInputLabel: {
    fontSize: 12,
    color: '#333',
    marginBottom: 6,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    backgroundColor: '#fff',
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: '#333',
  },
  inputError: {
    borderColor: '#dc3545',
  },
  errorText: {
    fontSize: 12,
    color: '#dc3545',
    marginTop: 4,
  },
  driverDocsRow: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
  },
  docCheckboxItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  docCheckboxLabel: {
    fontSize: 14,
    color: '#333',
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 20,
    paddingTop: 16,
    gap: 12,
  },
  cancelButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
    backgroundColor: '#6c757d',
  },
  cancelButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
  },
  sendButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
    backgroundColor: '#4A9EFF',
  },
  sendButtonDisabled: {
    backgroundColor: '#ccc',
  },
  sendButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
  },
  noPendingDocs: {
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  noPendingDocsText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  deleteModalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 5,
  },
  deleteModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  deleteModalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#000',
  },
  deleteModalBody: {
    padding: 24,
    alignItems: 'center',
  },
  deleteModalText: {
    fontSize: 16,
    color: '#333',
    textAlign: 'center',
    marginBottom: 8,
  },
  deleteModalDocName: {
    fontSize: 14,
    color: '#666',
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 12,
  },
  deleteModalWarning: {
    fontSize: 13,
    color: '#999',
    marginTop: 12,
    textAlign: 'center',
  },
  deleteModalSubtext: {
    fontSize: 14,
    color: '#666',
    marginTop: 8,
    textAlign: 'center',
  },
  deleteModalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 20,
    paddingTop: 16,
    gap: 12,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  deleteCancelButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
    backgroundColor: '#6c757d',
  },
  deleteCancelButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
  },
  deleteConfirmButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
    backgroundColor: '#dc3545',
  },
  deleteConfirmButtonDisabled: {
    backgroundColor: '#ccc',
  },
  deleteConfirmButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
  },
  editContactButton: {
    padding: 8,
    marginRight: 8,
  },
  deleteDriverButton: {
    padding: 8,
    marginRight: 8,
  },
  editContactModalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    maxWidth: 750,
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 5,
  },
  editContactModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  editContactModalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#000',
  },
  editContactModalBody: {
    padding: 24,
  },
  editContactDriverInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  editContactDriverName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  editContactHelperText: {
    fontSize: 13,
    color: '#666',
    marginBottom: 16,
    fontStyle: 'italic',
  },
  editContactInputGroup: {
    marginBottom: 16,
  },
  editContactLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 8,
  },
  editContactInput: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 6,
    backgroundColor: '#fff',
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    color: '#333',
  },
  editContactModalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 20,
    paddingTop: 16,
    gap: 12,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  editContactCancelButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
    backgroundColor: '#6c757d',
  },
  editContactCancelButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
  },
  editContactSaveButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
    backgroundColor: '#28a745',
  },
  editContactSaveButtonDisabled: {
    backgroundColor: '#ccc',
  },
  editContactSaveButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
  },
  driverFormRow: {
    marginBottom: 20,
  },
  driverFormHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  driverFormTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  driverFieldsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 10,
  },
  driverFieldContainer: {
    flex: 1,
    minWidth: 160,
    flexBasis: '30%',
  },
  driverFormDivider: {
    height: 1,
    backgroundColor: '#e0e0e0',
    marginTop: 16,
    marginBottom: 4,
  },
  loadingButton: {
    backgroundColor: '#2e96e1',
    borderRadius: 4,
    paddingVertical: 7,
    paddingHorizontal: 14,
    minWidth: 80,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const localStyles = StyleSheet.create({
  inputRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 10,
  },
  inputContainer: {
    // flex: 1 is applied inline
  },
  inputError: {
    borderColor: '#dc3545',
    borderWidth: 1,
  },
  driverFormRow: {
    marginBottom: 20,
  },
  driverFormHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  driverFormTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  driverFormDivider: {
    height: 1,
    backgroundColor: '#e0e0e0',
    marginTop: 16,
    marginBottom: 4,
  },
  addAnotherDriverButton: {
    color: '#2e96e1',
    fontSize: 14,
    textAlign: 'center',
  },
});

export default DocsTab;
